import { useCallback, useState } from "react";
import { StyleProp, StyleSheet, Text, TouchableOpacity, View, ViewStyle } from "react-native";
import { useSelector } from "react-redux";
import { kColors } from "../../kColors";
import { kFonts } from "../../kFonts";
import { kStyleConst } from "../../kStyle";
import { setTableConfig } from "../../redux/TableConfig/actions";
import { HorizontalRule, ShimmerView } from "..";
import ComboBox, { tOption } from "../ComboBox";
import { Checkbox, Gear } from "../impComponents";
import {
  tHeaderProps,
  tpGenerateCSV,
  tpTableColumnConfigMenu,
  tpTableColumnConfigMenuItem,
  tpTableTitleHeader,
  tRenderHeader,
  tRenderHeaderItem,
  tRenderItemContainer,
  tRenderRow,
  tSortColumn,
} from "./types";
import { tKey } from "../../redux/TableConfig/types";
import { PdChevronDown, PdChevronUp, PdCopy } from "../Icons";
import { useAppDispatch } from "src/redux";

export const sortColumn: tSortColumn = ({ myData, myColumnKey, myIsAscending, renderCell }) => {
  if (myColumnKey === undefined) return myData;
  const kSortedData = myData.sort((myRowA, myRowB) => {
    const aFirstVal = myRowA[myColumnKey];
    const aSecondVal = myRowB[myColumnKey];
    if (
      typeof aFirstVal === "object" &&
      !(aFirstVal instanceof Date) &&
      typeof aSecondVal === "object" &&
      !(aSecondVal instanceof Date)
    ) {
      const aFirstValStr = traverseJSX(renderCell(myColumnKey, myRowA));
      const aSecondValStr = traverseJSX(renderCell(myColumnKey, myRowB));
      if (aFirstValStr > aSecondValStr) return -1;
      if (aFirstValStr === aSecondValStr) return 0;
      return 1;
    }
    if (aFirstVal > aSecondVal) return -1;
    if (aFirstVal === aSecondVal) return 0;
    return 1;
  });
  if (myIsAscending) return kSortedData.reverse();
  return kSortedData;
};

export const RenderItemContainer: tRenderItemContainer = ({
  myCellKey,
  row,
  columnMinWidthConfig,
  _handleOnPress,
  renderCell,
}) => {
  const aColumnStyle: StyleProp<ViewStyle> = {};
  aColumnStyle.flexBasis = 1;
  aColumnStyle.flexGrow = 1;
  if (columnMinWidthConfig) {
    aColumnStyle.minWidth = columnMinWidthConfig;
  }

  if (_handleOnPress)
    return (
      <TouchableOpacity
        key={`${myCellKey}: ${row[myCellKey]}`}
        style={[styles.gTableCell, aColumnStyle]}
        onPress={() => _handleOnPress(myCellKey)}
      >
        {renderCell(myCellKey, row)}
      </TouchableOpacity>
    );
  return (
    <View key={`${myCellKey}: ${row[myCellKey]}`} style={[styles.gTableCell, aColumnStyle]}>
      {renderCell(myCellKey, row)}
    </View>
  );
};

export const RenderRow: tRenderRow = ({
  rowData,
  rowIndex,
  loading,
  onPress,
  shadeAlternates,
  columnOrder,
  renderCell,
  renderRowFooter,
  rowContainerStyle,
  columnMinWidthConfig,
  activeColumns,
  total,
}) => {
  const kActiveColumns = columnOrder.filter((myColumnKey) => !activeColumns || activeColumns[myColumnKey]);
  const _handleOnPress = useCallback(
    (myColumnKey) => {
      if (onPress) onPress(rowData, myColumnKey);
    },
    [onPress, rowData],
  );

  return (
    <ShimmerView loading={loading}>
      <View
        style={[
          styles.tableRow,
          rowIndex === 0 ? styles.tableRowFirstLine : {},
          rowIndex === total - 1 ? styles.tableRowLastLine : {},
          !!shadeAlternates && rowIndex % 2 === 1 ? styles.tableRowShaded : null,
          rowContainerStyle ? rowContainerStyle(rowData) : null,
        ]}
      >
        {kActiveColumns.map((myColumnKey) => (
          <RenderItemContainer
            key={`${myColumnKey}: ${rowData[myColumnKey]}`}
            myCellKey={myColumnKey}
            row={rowData}
            columnMinWidthConfig={columnMinWidthConfig}
            _handleOnPress={onPress ? _handleOnPress : undefined}
            renderCell={renderCell}
          />
        ))}
      </View>
      {renderRowFooter && renderRowFooter(rowData)}
    </ShimmerView>
  );
};

export const RenderCard: tRenderRow = ({
  rowData,
  loading,
  onPress,
  columnOrder,
  columnNames,
  renderCell,
  deviceSize,
  activeColumns,
}) => {
  const kActiveColumns = columnOrder.filter((myColumnKey) => !activeColumns || activeColumns[myColumnKey]);
  const _handleOnPress = useCallback(
    (myColumnKey) => {
      if (onPress) onPress(rowData, myColumnKey);
    },
    [onPress, rowData],
  );

  if (onPress) {
    return (
      <ShimmerView loading={loading}>
        {deviceSize === "xs" ? (
          <View style={styles.tableCard}>
            <View style={styles.tableSingleColumn}>
              {kActiveColumns.map((myColumnKey) => (
                <View key={`${myColumnKey}: ${rowData[myColumnKey]}`} style={styles.tableCardCellWithLabel}>
                  <TouchableOpacity onPress={() => _handleOnPress(myColumnKey)} style={styles.flexOne}>
                    <Text numberOfLines={1} ellipsizeMode="tail" style={styles.tableCardCellLabel}>
                      {columnNames[myColumnKey]}
                    </Text>
                    <View style={styles.gCardCell}>{renderCell(myColumnKey, rowData)}</View>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          </View>
        ) : (
          <View style={styles.tableCard}>
            <View style={styles.tableColLeft}>
              {kActiveColumns.map((myColumnKey, myIndex) => {
                if (myIndex % 2 === 0)
                  return (
                    <View key={`${myColumnKey}: ${rowData[myColumnKey]}`} style={styles.tableCardCellWithLabel}>
                      <TouchableOpacity onPress={() => _handleOnPress(myColumnKey)} style={styles.flexOne}>
                        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.tableCardCellLabel}>
                          {columnNames[myColumnKey]}
                        </Text>
                        <View style={styles.gCardCell}>{renderCell(myColumnKey, rowData)}</View>
                      </TouchableOpacity>
                    </View>
                  );
                else return <></>;
              })}
            </View>

            <View style={styles.tableColRight}>
              {kActiveColumns.map((myColumnKey, myIndex) => {
                if (myIndex % 2 === 1)
                  return (
                    <View key={`${myColumnKey}: ${rowData[myColumnKey]}`} style={styles.tableCardCellWithLabel}>
                      <TouchableOpacity onPress={() => _handleOnPress(myColumnKey)} style={styles.flexOne}>
                        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.tableCardCellLabel}>
                          {columnNames[myColumnKey]}
                        </Text>
                        <View style={styles.gCardCell}>{renderCell(myColumnKey, rowData)}</View>
                      </TouchableOpacity>
                    </View>
                  );
                else return <></>;
              })}
            </View>
          </View>
        )}
      </ShimmerView>
    );
  }
  return (
    <ShimmerView loading={loading}>
      <View style={styles.tableCard}>
        <View style={styles.tableColLeft}>
          {kActiveColumns.map((myColumnKey, myIndex) => {
            if (myIndex % 2 === 0)
              return (
                <View key={rowData.meta.key} style={styles.tableCardCellWithLabel}>
                  <Text style={styles.tableCardCellLabel}>{columnNames[myColumnKey]}</Text>
                  <View style={styles.gCardCell}>{renderCell(myColumnKey, rowData)}</View>
                </View>
              );
            else return <></>;
          })}
        </View>
        <View style={styles.tableColRight}>
          {kActiveColumns.map((myColumnKey, myIndex) => {
            if (myIndex % 2 === 1)
              return (
                <View key={rowData.meta.key} style={styles.tableCardCellWithLabel}>
                  <Text style={styles.tableCardCellLabel}>{columnNames[myColumnKey]}</Text>
                  <View style={styles.gCardCell}>{renderCell(myColumnKey, rowData)}</View>
                </View>
              );
            else return <></>;
          })}
        </View>
      </View>
    </ShimmerView>
  );
};

const HeaderBigItem: tRenderHeaderItem = ({
  myColumnKey,
  myColumnName,
  isSortable,
  currentSortConfig,
  _handleColumnSort,
  columnMinWidthConfig,
}) => {
  const _handleOnPress = useCallback(() => _handleColumnSort({ myColumnKey }), [_handleColumnSort, myColumnKey]);
  const aColumnStyle: StyleProp<ViewStyle> = {};
  aColumnStyle.flexBasis = 1;
  aColumnStyle.flexGrow = 1;
  if (columnMinWidthConfig) {
    aColumnStyle.minWidth = columnMinWidthConfig;
  }

  return (
    <View key={myColumnKey.toString()} style={[styles.tableHeaderCell, aColumnStyle]}>
      {isSortable ? (
        <TouchableOpacity style={styles.tableHeaderButton} onPress={_handleOnPress}>
          <Text
            style={
              currentSortConfig?.columnKey === myColumnKey ? styles.tableHeaderLabelSortActive : styles.tableHeaderLabel
            }
            numberOfLines={1}
            ellipsizeMode={"tail"}
          >
            {myColumnName}
          </Text>
          <View style={styles.gTableSortArrow}>
            {currentSortConfig?.columnKey === myColumnKey && currentSortConfig?.ascending ? (
              <PdChevronUp color={kColors.gray500} />
            ) : (
              <PdChevronDown color={kColors.gray500} />
            )}
          </View>
        </TouchableOpacity>
      ) : (
        <Text style={styles.tableHeaderLabel} numberOfLines={1} ellipsizeMode={"tail"}>
          {myColumnName}
        </Text>
      )}
    </View>
  );
};

export const HeaderBig: tRenderHeader = ({
  columnOrder,
  columnNames,
  sortConfig,
  _handleColumnSort,
  currentSortConfig,
  columnMinWidthConfig,
  activeColumns,
}) => {
  const kActiveColumns = columnOrder.filter((myColumnKey) => !activeColumns || activeColumns[myColumnKey]);
  return (
    <View style={styles.tableHeader}>
      {kActiveColumns.map((myColumnKey) => (
        <HeaderBigItem
          myColumnKey={myColumnKey}
          myColumnName={columnNames[myColumnKey]}
          isSortable={!!sortConfig && (sortConfig === "all" || sortConfig[myColumnKey])}
          currentSortConfig={currentSortConfig}
          columnMinWidthConfig={columnMinWidthConfig}
          _handleColumnSort={_handleColumnSort}
          key={myColumnKey.toString()}
        />
      ))}
    </View>
  );
};

export const HeaderSmall = <T, _C>({
  columnOrder,
  columnNames,
  _handleColumnSort,
  sortConfig,
}: tHeaderProps<T>): JSX.Element => {
  const [sSortColumnKey, _setSortColumnKey] = useState<keyof T | null>(null);
  const [sIsAscending, _setIsAscending] = useState<boolean>(true);
  const kSortColumns: Array<keyof T> = [];
  if (sortConfig === "all") {
    for (const myColumnKey of columnOrder) {
      kSortColumns.push(myColumnKey);
    }
  } else if (sortConfig) {
    for (const myColumnKey in sortConfig) {
      if (sortConfig[myColumnKey]) {
        kSortColumns.push(myColumnKey);
      }
    }
  }

  const kSortOptions: Array<tOption> = kSortColumns.map((myColumnKey) => ({
    myValue: myColumnKey.toString(),
    myLabel: columnNames[myColumnKey],
  }));
  const kOrderOptions: Array<tOption<"Ascending" | "Descending">> = [
    {
      myValue: "Ascending",
    },
    {
      myValue: "Descending",
    },
  ];

  const _handleOnSort = useCallback(
    (myValue, myIndex) => {
      _setSortColumnKey(myIndex);
      _handleColumnSort({ myColumnKey: myValue, isAscending: sIsAscending });
    },
    [_handleColumnSort, sIsAscending],
  );

  const _handleOnOrder = useCallback(
    (myOption, _) => {
      if (sSortColumnKey !== null) {
        _setIsAscending(myOption.myValue === "Ascending");
        _handleColumnSort({ myColumnKey: sSortColumnKey, isAscending: myOption.myValue === "Ascending" });
      }
    },
    [_handleColumnSort, sSortColumnKey],
  );

  if (kSortColumns?.length > 0)
    return (
      <>
        <View style={styles.gTableMobileSortOptionView1}>
          <Text style={styles.gTableMobileSortLabels}>Sort by...</Text>
          <ComboBox myOptions={kSortOptions} myPlaceholder="Sort by..." onSelect={_handleOnSort} />
        </View>
        <View style={styles.gTableMobileSortOptionView2}>
          <Text style={styles.gTableMobileSortLabels}>Order by...</Text>
          <ComboBox
            myOptions={kOrderOptions}
            myPlaceholder="Order by..."
            onSelect={_handleOnOrder}
            myDefaultIndex={0}
            disabled={sSortColumnKey === null}
          />
        </View>
      </>
    );
  return <></>;
};

export const traverseJSX = (element: JSX.Element): string => {
  let str = "";
  if (typeof element === "string") {
    return element;
  } else if (Array.isArray(element)) {
    element.forEach((myChild: JSX.Element) => {
      str = `${str} ${traverseJSX(myChild.props.children)}`;
    });
  } else if (typeof element === "object") {
    str = `${str} ${traverseJSX(element.props.children)}`;
  }
  return str.trim();
};

export const generateCSV: tpGenerateCSV = ({ data, columnNames, columnOrder, renderCell, activeColumns }) => {
  const table: Array<Array<string>> = [];
  const header: Array<string> = [];
  columnOrder.forEach((columnName) => {
    if (activeColumns) {
      if (activeColumns[columnName]) header.push(columnNames[columnName]);
    } else {
      header.push(columnNames[columnName]);
    }
  });
  table.push(header);
  data.forEach((myLine) => {
    const line: Array<string> = [];
    columnOrder.forEach((myCell) => {
      if (activeColumns) {
        if (activeColumns[myCell]) {
          const jsx = renderCell(myCell, myLine);
          const str = traverseJSX(jsx);
          line.push(str);
        }
      } else {
        const jsx = renderCell(myCell, myLine);
        const str = traverseJSX(jsx);
        line.push(str);
      }
    });
    table.push(line);
  });
  let csv = "";
  table.forEach((myLine, myRowIndex) => {
    let line = "";
    myLine.forEach((myCell, myCellIndex) => {
      if (myCellIndex === 0) {
        line = myCell;
      } else {
        line = `${line}\t${myCell}`;
      }
    });
    if (myRowIndex === 0) {
      csv = line;
    } else {
      csv = `${csv}\n${line}`;
    }
  });
  return csv;
};

const TableColumnConfigMenuItem: tpTableColumnConfigMenuItem = ({ column, columnName, isActive, onPress }) => {
  const handleOnPress = useCallback(() => onPress(column), [column, onPress]);
  return (
    <TouchableOpacity style={styles.tableColumnConfigOptionTO} onPress={handleOnPress}>
      <Checkbox style={styles.checkbox} state={isActive ? "on" : "off"} />
      <Text style={styles.tableColumnConfigOptionText}>{columnName}</Text>
    </TouchableOpacity>
  );
};

export const TableColumnConfigMenu: tpTableColumnConfigMenu = ({
  columnOrder,
  columnNames,
  onConfigure,
  activeColumns,
  innerRef,
  reduxKey,
}) => {
  const dispatch = useAppDispatch();

  let aIsAllSelectedDefault = true;
  let aIsNoneSelectedDefault = true;
  if (activeColumns) {
    Object.values(activeColumns).forEach((myVal) => {
      if (myVal === false) {
        aIsAllSelectedDefault = false;
      } else {
        aIsNoneSelectedDefault = false;
      }
    });
  }

  const [sIsAllSelected, _setIsAllSelected] = useState(aIsAllSelectedDefault);
  const [sIsNoneSelected, _setIsNoneSelected] = useState(activeColumns ? aIsNoneSelectedDefault : false);

  const handleSelectAll = useCallback(() => {
    const kNewConfig: Record<tKey, boolean> | undefined = {};
    columnOrder.forEach((myConfig) => {
      kNewConfig[myConfig] = true;
    });
    onConfigure(kNewConfig);
    _setIsAllSelected(true);
    if (sIsNoneSelected) _setIsNoneSelected(false);
    if (reduxKey) {
      dispatch(setTableConfig(reduxKey, kNewConfig));
    }
  }, [columnOrder, onConfigure, sIsNoneSelected, dispatch, reduxKey]);

  const handleToggleColumnState = useCallback(
    (column: keyof typeof columnNames) => {
      const kNewConfig: Record<tKey, boolean> = Object.assign({}, activeColumns);
      kNewConfig[column] = !kNewConfig[column];
      onConfigure(kNewConfig);
      if (reduxKey) {
        dispatch(setTableConfig(reduxKey, kNewConfig));
      }
      let aIsAllSelected = true;
      let aIsNoneSelected = true;
      Object.values(kNewConfig).forEach((myVal) => {
        if (myVal === false) {
          aIsAllSelected = false;
        } else {
          aIsNoneSelected = false;
        }
      });
      _setIsAllSelected(aIsAllSelected);
      _setIsNoneSelected(aIsNoneSelected);
    },
    [activeColumns, onConfigure, dispatch, reduxKey],
  );
  return (
    <View ref={innerRef} style={styles.tableColumnConfigMenu}>
      <Text style={styles.tableColumnConfigMenuText}>Select columns to view</Text>
      <View>
        <TouchableOpacity style={styles.tableColumnConfigOptionTO} onPress={handleSelectAll}>
          <Checkbox style={styles.checkbox} state={sIsAllSelected ? "on" : "off"} />
          <Text style={styles.tableColumnConfigOptionText}>Select All</Text>
        </TouchableOpacity>
        <HorizontalRule color={kColors.lightMed} />
        {columnOrder.map((myColumn) => {
          const isActive: boolean = activeColumns ? activeColumns[myColumn] : false;
          return (
            <TableColumnConfigMenuItem
              column={myColumn}
              columnName={columnNames[myColumn]}
              isActive={isActive}
              onPress={handleToggleColumnState}
              key={myColumn.toString()}
            />
          );
        })}
      </View>
    </View>
  );
};

export const TableTitleHeader: tpTableTitleHeader = ({
  title,
  canConfig,
  canCopyAsCSV,
  _handleCopyAsCSV,
  _handleToggleIsConfiguring,
  isConfiguring,
  menuRef,
  activeColumns,
  columnNames,
  columnOrder,
  setActiveColumns,
  reduxKey,
}) => {
  const rIsSmallDevice = useSelector((state) => state.NavigationReducer.deviceSize === "xs");
  return (
    <View style={[styles.between, styles.zIndexTen]}>
      <View>{title && <Text style={styles.sectionTitle}>{title}</Text>}</View>
      <View style={styles.buttons}>
        {canCopyAsCSV && (
          <TouchableOpacity style={styles.tableOptionTO} onPress={_handleCopyAsCSV}>
            {!rIsSmallDevice && <Text style={styles.configureTableText}>Copy as CSV</Text>}
            <PdCopy style={styles.configIcon} color={kColors.brandDark} />
          </TouchableOpacity>
        )}
        {canConfig && (
          <>
            <TouchableOpacity style={styles.tableOptionTO} onPress={_handleToggleIsConfiguring}>
              {!rIsSmallDevice && <Text style={styles.configureTableText}>Configure Table</Text>}
              <Gear style={styles.configIcon} color={kColors.brandDark} />
            </TouchableOpacity>
            {isConfiguring && (
              <TableColumnConfigMenu
                innerRef={menuRef}
                activeColumns={activeColumns}
                columnNames={columnNames}
                columnOrder={columnOrder}
                onConfigure={setActiveColumns}
                reduxKey={reduxKey}
              />
            )}
          </>
        )}
      </View>
    </View>
  );
};

type tpBuildEmptyActiveColumnConfig = <T>({ columnOrder }: { columnOrder: Array<keyof T> }) => Record<tKey, boolean>;
export const buildEmptyActiveColumnConfig: tpBuildEmptyActiveColumnConfig = ({ columnOrder }) => {
  const kEmptyConfig: Record<tKey, boolean> = {};
  columnOrder.forEach((myColumnKey) => {
    kEmptyConfig[myColumnKey] = true;
  });
  return kEmptyConfig;
};

const styles = StyleSheet.create({
  between: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginVertical: 6 * kStyleConst.kSpacing,
    position: "relative",
    zIndex: 1000,
  },
  buttons: {
    alignItems: "center",
    flexDirection: "row",
    zIndex: 10,
  },
  checkbox: {
    height: kStyleConst.kSpacing * 3,
    width: kStyleConst.kSpacing * 3,
  },
  configIcon: {
    height: 12,
    width: 12,
  },
  configureTableText: {
    ...kFonts.body,
    color: kColors.brandDark,
    marginEnd: kStyleConst.kSpacing,
  },
  flexOne: {
    flex: 1,
  },
  gCardCell: {
    alignItems: "flex-start",
    flexDirection: "row",
    justifyContent: "flex-start",
    paddingEnd: kStyleConst.kSpacing * 2,
    paddingVertical: kStyleConst.kSpacing * 2,
  },
  gTableCell: {
    alignItems: "flex-start",
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-start",
    overflow: "hidden",
    paddingRight: kStyleConst.kSpacing * 2,
    paddingVertical: kStyleConst.kSpacing * 2,
  },
  gTableMobileSortLabels: {
    ...kFonts.formLegend,
    marginVertical: kStyleConst.kSpacing,
  },
  gTableMobileSortOptionView1: {
    flexDirection: "column",
    marginTop: kStyleConst.kSpacing * 2,
    zIndex: 15,
  },
  gTableMobileSortOptionView2: {
    flexDirection: "column",
    marginTop: kStyleConst.kSpacing * 2,
    zIndex: 10,
  },
  gTableSortArrow: {
    ...kStyleConst.kTableSortArrowSizing,
    height: 10,
    justifyContent: "center",
    marginLeft: kStyleConst.kSpacing,
    width: 10,
  },
  sectionTitle: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 22,
    letterSpacing: 1.5,
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  tableCard: {
    flexDirection: "row",
    margin: 16,
    paddingHorizontal: 20,
    paddingTop: 16,
    shadowColor: kColors.lightBlack,
    shadowOffset: { width: 5, height: 5 },
    shadowRadius: 16,
  },
  tableCardCellLabel: {
    color: kColors.gray,
    overflow: "visible",
  },
  tableCardCellWithLabel: {
    alignItems: "stretch",
    flex: 1,
    marginBottom: kStyleConst.kSpacing * 3,
    overflow: "hidden",
  },
  tableColLeft: {
    flexDirection: "column",
    flex: 1,
    marginEnd: kStyleConst.kSpacing,
  },
  tableColRight: {
    flexDirection: "column",
    flex: 1,
    marginStart: kStyleConst.kSpacing,
  },
  tableColumnConfigMenu: {
    backgroundColor: kColors.brandLight,
    flex: 1,
    padding: kStyleConst.kSpacing * 4,
    position: "absolute",
    right: 10,
    top: kStyleConst.kSpacing * 8,
    width: kStyleConst.kSpacing * 50,
    zIndex: kStyleConst.kSpacing * 2,
  },
  tableColumnConfigMenuText: {
    ...kFonts.bodySmallCenter,
    fontWeight: "500",
    marginBottom: kStyleConst.kSpacing * 3,
    opacity: 0.5,
    textTransform: "uppercase",
  },
  tableColumnConfigOptionTO: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: kStyleConst.kSpacing * 2,
  },
  tableColumnConfigOptionText: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 11,
    fontWeight: "300",
    letterSpacing: 0.2,
    marginStart: kStyleConst.kSpacing * 3,
    textTransform: "uppercase",
  },
  tableHeader: {
    backgroundColor: kColors.white,
    borderBottomColor: kColors.brandPink,
    borderBottomWidth: 1,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  tableHeaderButton: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  tableHeaderCell: {
    alignItems: "flex-start",
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-start",
    overflow: "hidden",
    paddingVertical: kStyleConst.kSpacing * 1,
  },
  tableHeaderLabel: {
    ...kFonts.bodySmallCenter,
    color: kColors.gray500,
    fontWeight: "500",
    textTransform: "uppercase",
  },
  tableHeaderLabelSortActive: {
    ...kFonts.bodySmallCenter,
    color: kColors.gray500,
    fontWeight: "600",
    textTransform: "uppercase",
  },
  tableOptionTO: {
    alignItems: "center",
    borderRadius: kStyleConst.kConfigureButtonBorderWidth,
    flexDirection: "row",
    paddingHorizontal: kStyleConst.kSpacing * 2,
    paddingVertical: kStyleConst.kSpacing * 2,
    zIndex: 10,
  },
  tableRow: {
    alignItems: "center",
    backgroundColor: kColors.white,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  tableRowFirstLine: {
    paddingTop: 10,
  },
  tableRowLastLine: {
    borderBottomColor: kColors.brandPink,
    borderBottomWidth: 1,
    marginBottom: 40,
    paddingBottom: 10,
  },
  tableRowShaded: {
    backgroundColor: kColors.almostWhite,
  },
  tableSingleColumn: {
    flexDirection: "column",
    flex: 1,
  },
  zIndexTen: {
    zIndex: 10,
  },
});
